import { DeliveryInformation, Silo } from '@next/core-lib/cart';
import { createEntityAdapter, EntityAdapter, Update } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';

import * as appActions from './actions';
import { AppState, BannerNotificationState } from '../models/app.models';
import { CatalogItem } from '../models/catalog-item.model';
import { Order } from '../models/order.models';
import { RepeatOrderItem } from '../models/repeat-order-item.model';
import { SortMode } from '../models/sort-mode.model';

export const orderAdapter = createEntityAdapter<Order>({
  selectId: (order) => order.id,
});
export const catalogCategoryAdapter = createEntityAdapter<CatalogItem>({
  selectId: (catalogCategories) => catalogCategories.entryId,
});
export const catalogProductAdapter = createEntityAdapter<CatalogItem>({
  selectId: (catalogProducts) => catalogProducts.entryId,
});
export const catalogSubcategoryAdapter: EntityAdapter<CatalogItem> = createEntityAdapter<CatalogItem>({
  selectId: (catalogSubcategories) => catalogSubcategories.entryId,
});
export const repeatOrderBulkAdapter = createEntityAdapter<RepeatOrderItem>({
  selectId: (item) => item.internalId,
});
export const repeatOrderGeneralCargoAdapter = createEntityAdapter<RepeatOrderItem>({
  selectId: (item) => item.internalId,
});

export const initialState: AppState = {
  orders: orderAdapter.getInitialState([]),
  orderDetails: null,
  catalogCategories: catalogCategoryAdapter.getInitialState([]),
  catalogProducts: catalogProductAdapter.getInitialState([]),
  catalogSubcategories: catalogSubcategoryAdapter.getInitialState([]),
  selectedCatalogCategory: null,
  repeatOrderBulk: repeatOrderBulkAdapter.getInitialState([]),
  repeatOrderGeneralCargo: repeatOrderGeneralCargoAdapter.getInitialState([]),
  catalogProductSearchString: '',
  catalogSortMode: SortMode.default,
  catalogTotalNumberOfProductsFound: null,
};

export const bannerNotificationState: BannerNotificationState = {
  bannerNotificationVisible: true,
};

export const bannerNotificationReducer = createReducer<BannerNotificationState>(
  bannerNotificationState,
  on(appActions.setBannerNotificationVisibility, (state, { visible }) => ({
    ...state,
    bannerNotificationVisible: visible,
  })),
);

export const appReducer = createReducer<AppState>(
  initialState,
  on(appActions.ordersSuccess, (state, { orders }) => ({
    ...state,
    orders: orderAdapter.setAll(orders, state.orders),
  })),
  on(appActions.clearOrdersRequest, (state) => ({
    ...state,
    orders: orderAdapter.removeAll(state.orders),
  })),
  on(appActions.ordersFailure, (state) => ({
    ...state,
    orders: orderAdapter.removeAll(state.orders),
  })),
  on(appActions.orderDetailsSuccess, (state, { orderDetails }) => ({
    ...state,
    orderDetails,
  })),
  on(appActions.orderDetailsFailure, (state) => ({
    ...state,
    orderDetails: null,
  })),
  on(appActions.clearOrderDetailsRequest, (state) => ({
    ...state,
    orderDetails: null,
  })),
  on(appActions.catalogCategoriesSuccess, (state, { catalogCategories }) => ({
    ...state,
    catalogCategories: catalogCategoryAdapter.setAll(catalogCategories, state.catalogCategories),
  })),
  on(appActions.catalogCategoriesFailure, (state) => ({
    ...state,
    catalogCategories: catalogCategoryAdapter.removeAll(state.catalogCategories),
  })),
  on(appActions.catalogProductsSuccess, (state, { catalogProducts }) => ({
    ...state,
    catalogProducts: catalogProductAdapter.setAll(catalogProducts, state.catalogProducts),
  })),
  on(appActions.catalogProductsFailure, (state) => ({
    ...state,
    catalogProducts: catalogProductAdapter.removeAll(state.catalogProducts),
  })),
  on(appActions.setSelectedCatalogCategory, (state, { selectedCatalogCategory }) => ({
    ...state,
    selectedCatalogCategory,
  })),
  on(appActions.setCatalogProductSearchString, (state, { catalogProductSearchString }) => ({
    ...state,
    catalogProductSearchString,
  })),
  on(appActions.setCatalogSortMode, (state, { catalogSortMode }) => ({
    ...state,
    catalogSortMode,
  })),
  on(appActions.setCatalogTotalNumberOfProductsFound, (state, { catalogTotalNumberOfProductsFound }) => ({
    ...state,
    catalogTotalNumberOfProductsFound,
  })),
  on(appActions.catalogSubcategoriesSuccess, (state, { catalogSubcategories }) => ({
    ...state,
    catalogSubcategories: catalogSubcategoryAdapter.setAll(catalogSubcategories, state.catalogSubcategories),
  })),
  on(appActions.catalogSubcategoriesFailure, (state) => ({
    ...state,
    catalogSubcategories: catalogSubcategoryAdapter.removeAll(state.catalogSubcategories),
  })),
  on(appActions.repeatOrdersRequestSuccess, (state, { bulk, generalCargo }) => ({
    ...state,
    repeatOrderBulk: repeatOrderBulkAdapter.setAll(bulk, state.repeatOrderBulk),
    repeatOrderGeneralCargo: repeatOrderGeneralCargoAdapter.setAll(generalCargo, state.repeatOrderGeneralCargo),
  })),
  on(appActions.updateSiloDistribution, (state, { silos, internalId }) => ({
    ...state,
    repeatOrderBulk: repeatOrderBulkAdapter.updateOne(updatedBulkOrders(internalId, silos), state.repeatOrderBulk),
  })),
  on(appActions.updateBulkDeliveryInformation, (state, { deliveryInformation, internalId }) => ({
    ...state,
    repeatOrderBulk: repeatOrderBulkAdapter.updateOne(updatedDeliveryInformation(internalId, deliveryInformation), state.repeatOrderBulk),
  })),
  on(appActions.updateCargoDeliveryInformation, (state, { deliveryInformation, internalId }) => ({
    ...state,
    repeatOrderGeneralCargo: repeatOrderGeneralCargoAdapter.updateOne(
      updatedDeliveryInformation(internalId, deliveryInformation),
      state.repeatOrderGeneralCargo,
    ),
  })),
);

const updatedBulkOrders = (id: number, silos: Silo[]): Update<RepeatOrderItem> => ({
  id,
  changes: { silos },
});

const updatedDeliveryInformation = (id: number, deliveryInformation: DeliveryInformation) => ({
  id,
  changes: { deliveryInformation },
});

const repeatOrderBulkSelectors = repeatOrderBulkAdapter.getSelectors(({ app }) => app.repeatOrderBulk);

export const selectAllRepeatOrderBulk = repeatOrderBulkSelectors.selectAll;

const repeatOrderGeneralCargoSelectors = repeatOrderGeneralCargoAdapter.getSelectors(({ app }) => app.repeatOrderGeneralCargo);

export const selectAllRepeatOrderGeneralCargo = repeatOrderGeneralCargoSelectors.selectAll;
